// Painel lateral compactado que mostra os anexos do negócio (PDFs, KMZ, briefings).
// Usado dentro dos modais de Nova Composição e Gerar Proposta — assim o usuário
// pode consultar o briefing original sem fechar o modal.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { API_URL } from '../config';

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

interface Anexo {
  id: string;
  nome: string;
  tipo_arquivo: string;
  tamanho_bytes: number | null;
  criado_em: string;
}

const ICONES: Record<string, string> = {
  pdf: '📄', kmz: '🗺️', kml: '🗺️', image: '🖼️', doc: '📝',
  sheet: '📊', archive: '🗜️', other: '📎',
};

export default function PainelAnexosNegocio({ negocioId }: { negocioId: number }) {
  const [anexos, setAnexos] = useState<Anexo[]>([]);
  const [loading, setLoading] = useState(true);
  const [colapsado, setColapsado] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'anexos_list', negocio_id: negocioId },
          { headers: { Authorization: token } });
        const data = r.data.body ? JSON.parse(r.data.body) : r.data;
        setAnexos(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Erro carregando anexos no painel:', e);
      } finally { setLoading(false); }
    })();
  }, [negocioId]);

  async function abrir(a: Anexo) {
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'anexos_download_url', id: a.id },
        { headers: { Authorization: token } });
      const data = r.data.body ? JSON.parse(r.data.body) : r.data;
      window.open(data.download_url, '_blank');
    } catch (e: any) {
      alert('Erro ao abrir anexo: ' + (e.response?.data?.error || e.message));
    }
  }

  function fmtBytes(b: number | null): string {
    if (!b) return '';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
  }

  if (colapsado) {
    return (
      <button onClick={() => setColapsado(false)}
        title={`${anexos.length} anexo(s) do negócio`}
        style={{
          position: 'absolute', top: 14, right: 14, zIndex: 5,
          padding: '6px 10px', fontSize: 11, fontWeight: 600,
          border: '0.5px solid #CDD4EA', background: '#fff', color: '#0930D0',
          borderRadius: 5, cursor: 'pointer',
        }}>
        📎 Anexos ({anexos.length})
      </button>
    );
  }

  return (
    <div style={{
      width: 240, flexShrink: 0,
      borderLeft: '0.5px solid #e0e0e0',
      background: '#FAFBFF',
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
    }}>
      <div style={{
        padding: '10px 12px', borderBottom: '0.5px solid #e0e0e0',
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 11, fontWeight: 700, color: '#020D7F', background: '#fff',
      }}>
        📎 Anexos do negócio
        <span style={{ marginLeft: 'auto', fontSize: 10, color: '#888', fontWeight: 400 }}>
          {anexos.length}
        </span>
        <button onClick={() => setColapsado(true)} title="Recolher"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: 14 }}>
          ›
        </button>
      </div>
      <div style={{ padding: '8px 6px', flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ padding: 14, fontSize: 11, color: '#888', textAlign: 'center' }}>Carregando…</div>
        ) : anexos.length === 0 ? (
          <div style={{ padding: 14, fontSize: 11, color: '#aaa', textAlign: 'center', fontStyle: 'italic' }}>
            Nenhum anexo neste negócio.<br />
            Suba briefings PDF, KMZ ou e-mails do cliente na aba <strong>Anexos</strong>.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {anexos.map(a => (
              <button key={a.id} onClick={() => abrir(a)}
                title={`Abrir "${a.nome}" em nova aba`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '7px 9px', borderRadius: 4,
                  border: 'none', background: 'transparent',
                  cursor: 'pointer', textAlign: 'left',
                  width: '100%',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = '#EFF3FF')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>
                  {ICONES[a.tipo_arquivo] || ICONES.other}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 11, color: '#0930D0', fontWeight: 500,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {a.nome}
                  </div>
                  <div style={{ fontSize: 9, color: '#888' }}>
                    {fmtBytes(a.tamanho_bytes)}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      <div style={{
        padding: '6px 10px', borderTop: '0.5px solid #e0e0e0',
        fontSize: 9, color: '#888', background: '#fff',
      }}>
        💡 Clique em um anexo para abrir em nova aba.
      </div>
    </div>
  );
}
